import * as XLSX from 'xlsx';
import { Capacitor } from '@capacitor/core';
import { Directory, Filesystem } from '@capacitor/filesystem';
import { Share } from '@capacitor/share';

const getReportRows = (flats) => flats.map((flat) => ({
  'Flat Number': flat.flatNumber || '',
  'Outstanding Balance': Number(flat.outstandingBalance) || 0
}));

const getReportLabel = (isPendingReport) => isPendingReport
  ? 'Pending Flats Report'
  : 'Flat Outstanding Balance Report';

const getReportTitle = (buildingName, isPendingReport) => (
  `${buildingName?.trim() || 'Building'} - ${getReportLabel(isPendingReport)}`
);

const getFileName = (buildingName, extension) => {
  const date = new Date().toISOString().slice(0, 10);
  const safeBuildingName = (buildingName?.trim() || 'building')
    .replace(/[^a-z0-9]+/gi, '-')
    .replace(/^-|-$/g, '')
    .toLowerCase();
  return `${safeBuildingName}-flat-outstanding-report-${date}.${extension}`;
};

const downloadBlob = (blob, fileName) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};

const shareNativeFile = async (base64Data, fileName) => {
  const result = await Filesystem.writeFile({
    path: `reports/${fileName}`,
    data: base64Data,
    directory: Directory.Cache,
    recursive: true
  });

  await Share.share({
    title: fileName,
    dialogTitle: 'Save or share report',
    files: [result.uri]
  });
};

const textToBase64 = (text) => {
  const bytes = new TextEncoder().encode(text);
  let binary = '';
  const chunkSize = 0x8000;
  for (let offset = 0; offset < bytes.length; offset += chunkSize) {
    binary += String.fromCharCode(...bytes.subarray(offset, offset + chunkSize));
  }
  return window.btoa(binary);
};

export const downloadFlatOutstandingExcel = async (flats, buildingName, isPendingReport = false) => {
  const reportRows = getReportRows(flats);
  const worksheet = XLSX.utils.aoa_to_sheet([
    [getReportTitle(buildingName, isPendingReport)],
    ['Flat Number', 'Outstanding Balance'],
    ...reportRows.map(row => [row['Flat Number'], row['Outstanding Balance']])
  ]);
  worksheet['!merges'] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: 1 } }];
  worksheet['!cols'] = [{ wch: 18 }, { wch: 24 }];

  for (let row = 3; row <= flats.length + 2; row += 1) {
    const cell = worksheet[`B${row}`];
    if (cell) cell.z = '₹#,##0.00';
  }

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Outstanding Balances');
  const fileName = getFileName(buildingName, 'xlsx');
  if (Capacitor.isNativePlatform()) {
    const base64Data = XLSX.write(workbook, { bookType: 'xlsx', type: 'base64' });
    await shareNativeFile(base64Data, fileName);
    return;
  }
  XLSX.writeFile(workbook, fileName);
};

const escapePdfText = (value) => String(value)
  .replace(/\\/g, '\\\\')
  .replace(/\(/g, '\\(')
  .replace(/\)/g, '\\)');

const buildPageContent = (rows, pageNumber, totalPages, buildingName, isPendingReport) => {
  const commands = [
    'BT',
    '/F2 16 Tf',
    '50 800 Td',
    `(${escapePdfText(getReportTitle(buildingName, isPendingReport))}) Tj`,
    '/F1 9 Tf',
    `0 -18 Td (Generated: ${new Date().toLocaleDateString('en-IN')}) Tj`,
    '/F2 11 Tf',
    '0 -30 Td (Flat Number) Tj',
    '260 0 Td (Outstanding Balance) Tj',
    '/F1 10 Tf'
  ];

  rows.forEach((row) => {
    commands.push('-260 -20 Td');
    commands.push(`(${escapePdfText(row['Flat Number'])}) Tj`);
    commands.push('260 0 Td');
    commands.push(`(Rs. ${row['Outstanding Balance'].toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}) Tj`);
  });

  commands.push('/F1 8 Tf');
  commands.push('-260 -28 Td');
  commands.push(`(Page ${pageNumber} of ${totalPages}) Tj`);
  commands.push('ET');
  return commands.join('\n');
};

const createPdf = (rows, buildingName, isPendingReport) => {
  const rowsPerPage = 32;
  const pages = [];
  for (let index = 0; index < rows.length; index += rowsPerPage) {
    pages.push(rows.slice(index, index + rowsPerPage));
  }
  if (pages.length === 0) pages.push([]);

  const objects = {};
  const pageObjectNumbers = pages.map((_, index) => 5 + index * 2);
  objects[1] = '<< /Type /Catalog /Pages 2 0 R >>';
  objects[2] = `<< /Type /Pages /Kids [${pageObjectNumbers.map(number => `${number} 0 R`).join(' ')}] /Count ${pages.length} >>`;
  objects[3] = '<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica >>';
  objects[4] = '<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica-Bold >>';

  pages.forEach((pageRows, index) => {
    const pageObject = pageObjectNumbers[index];
    const contentObject = pageObject + 1;
    const content = buildPageContent(pageRows, index + 1, pages.length, buildingName, isPendingReport);
    objects[pageObject] = `<< /Type /Page /Parent 2 0 R /MediaBox [0 0 595 842] /Resources << /Font << /F1 3 0 R /F2 4 0 R >> >> /Contents ${contentObject} 0 R >>`;
    objects[contentObject] = `<< /Length ${content.length} >>\nstream\n${content}\nendstream`;
  });

  const objectCount = 4 + pages.length * 2;
  let pdf = '%PDF-1.4\n';
  const offsets = [0];
  for (let number = 1; number <= objectCount; number += 1) {
    offsets[number] = pdf.length;
    pdf += `${number} 0 obj\n${objects[number]}\nendobj\n`;
  }

  const xrefOffset = pdf.length;
  pdf += `xref\n0 ${objectCount + 1}\n`;
  pdf += '0000000000 65535 f \n';
  for (let number = 1; number <= objectCount; number += 1) {
    pdf += `${String(offsets[number]).padStart(10, '0')} 00000 n \n`;
  }
  pdf += `trailer\n<< /Size ${objectCount + 1} /Root 1 0 R >>\nstartxref\n${xrefOffset}\n%%EOF`;
  return pdf;
};

export const downloadFlatOutstandingPdf = async (flats, buildingName, isPendingReport = false) => {
  const rows = getReportRows(flats);
  const pdf = createPdf(rows, buildingName, isPendingReport);
  const fileName = getFileName(buildingName, 'pdf');
  if (Capacitor.isNativePlatform()) {
    await shareNativeFile(textToBase64(pdf), fileName);
    return;
  }
  downloadBlob(new Blob([pdf], { type: 'application/pdf' }), fileName);
};
